// Third-party
import React, { Component } from 'react';
import { connect } from 'react-redux';

// Components
import DataTable from '../../../components/data/DataTable';
import TablePageLayout from '@/views/containers/layouts/TablePageLayout';
import { AddEnterpriseGroupModal } from '../../../components/modals';
import { DestructiveModal } from '@capintel/component-lib/destructive-modal';
import { Stack } from '@capintel/component-lib/stack';
import { GenericEmptyState } from '../../../components/misc';
import { IconButton, PrimaryButton } from '../../../components/buttons';
import { SearchInput } from '../../../components/inputs';
import { SecondaryNavigation } from '@/views/components/navigation';

// Utils
import { formatMessage, FormattedMessage } from '../../../../utils/i18n';
import { searchObjectsOnFields } from '../../../../utils/search';
import { isEnterpriseGroup, ENTERPRISE_GROUP_NAME } from '../../../../utils/enterprise';
import { addEnterpriseGroup, deleteEnterpriseGroup } from '../../../../redux/modules/enterprise/admin/groups';
import { PERMISSIONS, userHasPermission, userHasPermissionEnabled } from '../../../../utils/permissions';

// Types
import { ReduxState } from '../../../../redux/modules/types';
import EnterpriseAdminDetails from '@/types/enterprise-admin-details';
import EnterpriseDetails from '@/types/enterprise-detail-info';
import EnterpriseGroup from '@/types/enterprise-group';
import NavigationItem from '@/types/navigation-item';
import Nullable from '@/types/nullable';
import User from '@/types/user';

// TODO: @deprecate when we deprecate Legacy
import LegacyEnterpriseGroups from './LegacyEnterpriseGroups';
import useThemeVersion from '@/hooks/useThemeVersion';

type State = {
  showAddGroupModal: boolean;
  showDeleteGroupsModal: boolean;
  groupsToDelete: EnterpriseGroup[];
  query: string;
  expanded?: EnterpriseGroup;
};

type DispatchProps = {
  addEnterpriseGroup: (name: string) => Promise<EnterpriseGroup>;
  deleteEnterpriseGroup: (groupId: string) => Promise<void>;
};

type StateProps = {
  enterprise: EnterpriseDetails;
  adminDetails: EnterpriseAdminDetails;
  user?: Nullable<User>;
};

type ParentProps = {
  pills?: NavigationItem[]; // Optional for now due to legacy components. Will be required after legacy components are removed.
  onRowClick?: (id: string) => void;
};

type Props = StateProps & ParentProps & DispatchProps;

class EnterpriseGroups extends Component<Props, State> {
  readonly state: State = {
    showAddGroupModal: false,
    showDeleteGroupsModal: false,
    groupsToDelete: [],
    query: '',
  };

  removeSelectedGroups = () => {
    const { user } = this.props;
    const { groupsToDelete } = this.state;

    if (!user) {
      return;
    }

    this.hideDeleteGroupsModal();

    for (const group of groupsToDelete) {
      this.props.deleteEnterpriseGroup(group._id);
    }
  };

  deleteGroupById = (id: string) => {
    this.props.deleteEnterpriseGroup(id);
  };

  allSelectableItems = () => {
    const { adminDetails } = this.props;
    const localGroups = (adminDetails && adminDetails.groups) || [];

    const { query } = this.state;
    if (query !== '') {
      return this.filterGroupsFromQuery(localGroups, query).filter(x => !isEnterpriseGroup(x));
    }
    return localGroups.filter(x => !isEnterpriseGroup(x));
  };

  showDeleteGroupsModal = (groupsToDelete: EnterpriseGroup[]) => {
    this.setState(() => ({
      showDeleteGroupsModal: true,
      groupsToDelete,
    }));
  };

  showAddGroupModal = () => {
    this.setState({ showAddGroupModal: true });
  };

  hideAddGroupModal = () => {
    this.setState({ showAddGroupModal: false });
  };

  hideDeleteGroupsModal = () => {
    this.setState({ showDeleteGroupsModal: false });
  };

  filterGroupsFromQuery = (groups: EnterpriseGroup[], query: string) => {
    if (!query) {
      return groups;
    }

    const searchableFields = ['name'];

    // Generate fullname for search purposes.
    const objs = groups.map(m => ({
      ...m,
      fullname: m.name,
    }));

    return searchObjectsOnFields(query, objs, searchableFields, false);
  };

  onGroupsSearch = (query: string) => {
    this.setState({ query });
  };

  getColumns = (): any[] => {
    const { user } = this.props;
    const hasGroupAdminPermission = userHasPermission(user, PERMISSIONS.ENTERPRISE_ADMIN_GROUPS);
    const hasMemberAdminPermission = userHasPermissionEnabled(
      user,
      PERMISSIONS.ENTERPRISE_ADMIN_GROUPS_MEMBERS,
      PERMISSIONS.ENTERPRISE_ADMIN_GROUPS_MEMBERS_ADD,
      PERMISSIONS.ENTERPRISE_ADMIN_GROUPS_MEMBERS_REMOVE,
    );

    const columns = [
      {
        Header: formatMessage('Admin.Enterprise.Fields.GroupName'),
        accessor: 'name',
        className: 'enterprise-group-name',
        width: hasMemberAdminPermission ? 150 : undefined,
      },
      {
        id: 'colMembers',
        Header: formatMessage('Admin.Enterprise.TeamMembers'),
        className: 'enterprise-group-name',
        accessor: (group: EnterpriseGroup) => formatMessage('Admin.Enterprise.TeamMembersCount', { count: group.members.length }),
        show: hasMemberAdminPermission,
      },
      {
        accessor: '_id',
        className: 'icon-cell',
        Cell: ({ original }: { original: EnterpriseGroup }) => (
          <IconButton
            icon="ds-delete"
            onClick={() => {
              this.showDeleteGroupsModal([original]);
            }}
          />
        ),
        width: 48,
        show: hasGroupAdminPermission,
      },
    ];

    return columns;
  };

  onAddGroupToEnterprise = (name: string) =>
    // Reload enterprise's data after adding a new group
    this.props.addEnterpriseGroup(name).then(
      newGroup =>
        // TODO - get groups
        // this.props.getEnterprise(enterprise._id);
        newGroup,
    );

  onRemoveGroupFromEnterprise = (groupId: string) => this.props.deleteEnterpriseGroup(groupId);

  getEmptyStateCard = (localGroups: EnterpriseGroup[]) => {
    const hasGroups = localGroups && localGroups.length > 0;
    if (localGroups.length === 0)
      return (
        <div className="empty-results">
          <GenericEmptyState header={formatMessage('Admin.Enterprise.NoGroups.Title')} body={formatMessage('Admin.Enterprise.NoGroups.Text')} />
        </div>
      );
    if (localGroups.length > 0 && !hasGroups)
      return (
        <div className="empty-results">
          <h1>
            <FormattedMessage id="Admin.Enterprise.NoResults.Title" />
          </h1>
          <span className="empty-results-text">
            <FormattedMessage id="Admin.Enterprise.NoResults.Text" />
          </span>
        </div>
      );
  };

  render() {
    const { showAddGroupModal, showDeleteGroupsModal, query } = this.state;
    const { adminDetails, onRowClick, user } = this.props;

    const localGroups = (adminDetails && adminDetails.groups.filter(g => g.name !== ENTERPRISE_GROUP_NAME)) || [];
    const filteredGroups = this.filterGroupsFromQuery(localGroups, query);

    return (
      <>
        <div className="enterprise-groups">
          <TablePageLayout
            mainClassName="enterprise-groups-table"
            pageTitle="Admin.Enterprise.Groups.Title"
            pillsFilterElement={this.props.pills && <SecondaryNavigation className="navigation-pills" items={this.props.pills} />}
            searchInputElement={
              <SearchInput
                className="settings-search"
                value={this.state.query}
                onChange={this.onGroupsSearch}
                placeholder={formatMessage('Admin.Enterprise.SearchGroups')}
              />
            }
            pageActionsElement={
              <PrimaryButton onClick={this.showAddGroupModal} disabled={!userHasPermission(user, PERMISSIONS.ENTERPRISE_ADMIN_GROUPS)}>
                <i className="icon-plus" />
                <FormattedMessage id="Admin.Enterprise.AddGroup" />
              </PrimaryButton>
            }
            hasData={filteredGroups.length > 0}
            noDataElement={this.getEmptyStateCard(localGroups)}
            tableElement={
              <DataTable data={filteredGroups} className="results-table flex-grow-1" columns={this.getColumns()} canSelect={false} onRowClick={onRowClick} />
            }
          />
        </div>

        <AddEnterpriseGroupModal show={showAddGroupModal} onCancel={this.hideAddGroupModal} onSave={this.onAddGroupToEnterprise} />

        <DestructiveModal
          open={showDeleteGroupsModal}
          onOpenChange={open => !open && this.hideDeleteGroupsModal()}
          title={formatMessage('Admin.Enterprise.DeleteGroupsModal.Title')}
          content={
            <Stack gap="150">
              <FormattedMessage id="Admin.Enterprise.DeleteGroupsModal.Body" />
              <ul>
                {this.state.groupsToDelete.map(g => (
                  <li key={g.name}>{g.name}</li>
                ))}
              </ul>
              <FormattedMessage id="Admin.Enterprise.DeleteGroupsModal.Confirmation" />
            </Stack>
          }
          confirmButtonText={formatMessage('Modal.Default.Confirm')}
          cancelButtonText={formatMessage('Common.Cancel')}
          onConfirm={this.removeSelectedGroups}
          onCancel={this.hideDeleteGroupsModal}
        />
      </>
    );
  }
}

const EnterpriseGroupsComponent = (props: Props) => {
  const { isLightVersion } = useThemeVersion();
  if (isLightVersion) {
    return <EnterpriseGroups {...props} />;
  }
  return <LegacyEnterpriseGroups {...props} />;
};

const mapStateToProps = (state: ReduxState) => ({
  user: state.auth.user,
  adminDetails: state.enterprise.admin,
  enterprise: state.enterprise.details,
});

export default connect(mapStateToProps, {
  addEnterpriseGroup,
  deleteEnterpriseGroup,
})(EnterpriseGroupsComponent);
